/**
 * Task #1966 — First-Run Setup substrate.
 *
 * Persists every guided-setup step transition + every Setup Health
 * sweep as a tamper-evident, HMAC-signed manifest row. Read by both
 * the SA Guided Platform Setup wizard and the headless
 * `npm run setup:health` CLI.
 *
 * NOTE: New shared/ schema file — isolated to a single new module
 * per the "minimise shared/ deviation" precedent set by Task #1960.
 */
import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  timestamp,
  jsonb,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

export const setupHistory = pgTable(
  "setup_history",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    /** "wizard" — guided wizard step transition; "health" — re-run health sweep; "complete" — final commit. */
    kind: text("kind").notNull(),
    /** SetupStep id ("env-health", "secrets", …) — null for "health" sweeps that span all checks. */
    stepId: text("step_id"),
    /** Aggregate status: pass | warn | fail. */
    status: text("status").notNull(),
    /** Actor user id (nullable for CLI runs). */
    actorId: text("actor_id"),
    /** Actor display label (email/username at time of capture). */
    actorLabel: text("actor_label"),
    /** Source of the entry: "ui" | "cli" | "boot". */
    source: text("source").notNull().default("ui"),
    /** Per-check or per-step results captured at this point in time. */
    manifest: jsonb("manifest").$type<Record<string, unknown>>().notNull(),
    /** SHA-256(JSON.stringify(manifest)) at capture time. */
    manifestHash: text("manifest_hash").notNull(),
    /** HMAC-SHA-256 over { kind, stepId, status, manifestHash, capturedAt }. */
    signature: text("signature").notNull(),
    /** ISO timestamp the row represents (server-emitted). */
    capturedAt: timestamp("captured_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    idxSetupHistoryCapturedAt: index("idx_setup_history_captured_at").on(
      t.capturedAt,
    ),
    idxSetupHistoryKind: index("idx_setup_history_kind").on(t.kind),
  }),
);

export const insertSetupHistorySchema = createInsertSchema(setupHistory).omit({
  id: true,
  capturedAt: true,
});
export type InsertSetupHistory = z.infer<typeof insertSetupHistorySchema>;
export type SetupHistory = typeof setupHistory.$inferSelect;

/** Canonical platform_settings key signalling first-run completion. */
export const SETUP_COMPLETED_AT_KEY = "platform_setup_completed_at";
